import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendSms, sendEmailCode, submitSmsCode, loginWithTaskId, saveTokens } from '../../../api/submitSmsCode';
import '../style/SmsCode.css';
import logo from '../../../app_icon.svg';
import Modal from '../../../common/components/MessageModal';
import { t } from 'i18next';
import monitoringService from '../../../services/monitoringService';

const SmsCode = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { taskId } = location.state || {};
    const { phoneNumber } = location.state || {};
    const { typeAuth } = location.state || {};
    const { email } = location.state || {};
    const { hasEmailOption } = location.state || false;
    const [code, setCode] = useState(new Array(6).fill(''));
    const [timer, setTimer] = useState(30);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isEmailRequested, setIsEmailRequested] = useState(false);
    const [isRequestingEmail, setIsRequestingEmail] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (timer > 0) {
            const countdown = setTimeout(() => setTimer(timer - 1), 1000);
            return () => clearTimeout(countdown);
        }
    }, [timer]);

    useEffect(() => {
        const accessToken = localStorage.getItem('access_token');
        if (accessToken) {
            navigate('/profile#walletTopUp');
        } else {
            sendSms(taskId).catch((err) => {
                setError(err.message);
                setShowModal(true);
            });
        }
    }, [navigate, taskId]);

    const handleBackClick = () => {
        navigate('/sign');
    };

    const handleChange = (element, index) => {
        if (isNaN(element.value)) return;
        const newCode = [...code];
        newCode[index] = element.value;
        setCode(newCode);

        if (element.nextSibling) {
            element.nextSibling.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !code[index] && e.target.previousSibling) {
            e.target.previousSibling.focus();
        }
    };

    const handleSmsSubmit = async (e) => {
        e.preventDefault();
        const smsCode = code.join('');

        try {
            const task_id_from_response1 = await submitSmsCode(smsCode, taskId, phoneNumber);

            const loginData = await loginWithTaskId(task_id_from_response1, typeAuth);

            saveTokens(loginData.access_token, loginData.refresh_token, loginData.biometric_token);

            // Track successful login/registration
            if (typeAuth === 'login') {
                monitoringService.trackLogin(phoneNumber, true, null).catch(err => {
                    console.warn('Failed to track login success:', err);
                });
            } else if (typeAuth === 'signup') {
                monitoringService.trackRegistration(phoneNumber, true, null).catch(err => {
                    console.warn('Failed to track registration success:', err);
                });
            }

            // Set user info in monitoring service
            if (phoneNumber) {
                monitoringService.setPhoneNumber(phoneNumber);
            }
            // Try to get user ID from API
            monitoringService.fetchUserPhoneNumber().catch(err => {
                console.warn('Failed to fetch user info:', err);
            });

            // Dispatch userLogin event to trigger device registration
            window.dispatchEvent(new CustomEvent('userLogin', {
                detail: {
                    accessToken: loginData.access_token
                }
            }));

            navigate('/');
        } catch (error) {
            const errorMessage = error.message || 'Ошибка проверки кода.';
            // Track failed login/registration
            if (typeAuth === 'login') {
                monitoringService.trackLogin(phoneNumber, false, errorMessage).catch(err => {
                    console.warn('Failed to track login failure:', err);
                });
            } else if (typeAuth === 'signup') {
                monitoringService.trackRegistration(phoneNumber, false, errorMessage).catch(err => {
                    console.warn('Failed to track registration failure:', err);
                });
            }
            setError(errorMessage);
            setShowModal(true);
        }
    };

    const handleResendCode = () => {
        setTimer(30);
        setCode(new Array(6).fill(''));
        setIsEmailRequested(false);
        sendSms(taskId).catch((err) => {
            setError(err.message || 'Ошибка при отправке SMS.');
            setShowModal(true);
        });
    };

    const handleRequestEmailCode = async () => {
        if (!hasEmailOption || !email) {
            setError(t('emailNotAvailable') || 'Email недоступен');
            setShowModal(true);
            return;
        }

        setIsRequestingEmail(true);
        try {
            await sendEmailCode(taskId);
            setIsEmailRequested(true);
            setError('');
            // Show success message
            setError(t('emailCodeSent') || 'Код отправлен на email');
            setShowModal(true);
        } catch (err) {
            setError(err.message || 'Ошибка при отправке кода на email');
            setShowModal(true);
        } finally {
            setIsRequestingEmail(false);
        }
    };


    const handleCloseModal = () => {
        setShowModal(false);
        setError('');
    };

    return (
        <div className="sign_page">
            <div className="left_section"></div>

            <div className="right_section">
                <div className="right_section_header">
                    <button onClick={handleBackClick} className="back_button">
                        <i className="back_icon fa-solid fa-chevron-left"></i>
                        {isMobile ? t('enter_code') : t('back')}
                    </button>

                    {!isMobile && (
                        <div className="logo_container">
                            <img src={logo} alt="Agroland" className="logo" />
                        </div>
                    )}
                </div>
                <form onSubmit={handleSmsSubmit} className="sign_form">
                    {isMobile && (
                        <div className="logo_container">
                            <img src={logo} alt="Logo" className="form_logo" />
                        </div>
                    )}
                    {!isMobile && <h2 className="title">{t('enter_code')}</h2>}
                    <p className="sms_description">
                        {isEmailRequested 
                            ? (t('otpSentEmail') || 'Код отправлен на email')
                            : (t('otpSent') || 'Код отправлен на номер телефона')
                        }
                    </p>
                    {email && hasEmailOption && (
                        <p className="email_display">
                            {t('emailDisplay') || 'Email'}: {email}
                        </p>
                    )}
                    <div className="code_form">
                        {code.map((value, index) => (
                            <input
                                key={index}
                                type="text"
                                maxLength="1"
                                value={value}
                                onChange={(e) => handleChange(e.target, index)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                onFocus={(e) => e.target.select()}
                                className="code-input"
                            />
                        ))}
                    </div>
                    <button type="submit" className="submit_button">
                        {t('next')}
                    </button>
                </form>

                <div className="resend_section">
                    <p
                        className="click"
                        onClick={handleResendCode}
                        disabled={timer > 0}
                        style={{ opacity: timer > 0 ? 0.5 : 1, cursor: timer > 0 ? 'not-allowed' : 'pointer' }}
                    >
                        {t('resendOtp')}
                    </p>
                    <p className="timer">{timer}</p>
                </div>
                {hasEmailOption && email && (
                    <div className="email_code_section">
                        <button
                            type="button"
                            className="email_code_button"
                            onClick={handleRequestEmailCode}
                            disabled={isRequestingEmail}
                        >
                            {isRequestingEmail 
                                ? (t('sending') || 'Отправка...')
                                : (t('requestEmailCode') || 'Получить код на email')
                            }
                        </button>
                    </div>
                )}
            </div>

            <Modal show={showModal} message={error} onClose={handleCloseModal} />
        </div>
    );
};

export default SmsCode;